import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Combobox, ComboboxButton, ComboboxInput, ComboboxOption, ComboboxOptions } from '@headlessui/react';
import { ChevronDownIcon } from '@heroicons/react/20/solid';
import { supabase, upsertProjectConfiguration, updateProject } from '../utils/supabaseClient';
import { ExclamationTriangleIcon, PencilIcon, PlusIcon } from '@heroicons/react/24/outline';
import { XIcon, Check, Trash2 } from 'lucide-react';
import ProjectSharing from './project/ProjectSharing';

// US States for dropdown
const US_STATES = [
  { value: 'AL', label: 'Alabama' }, { value: 'AK', label: 'Alaska' }, { value: 'AZ', label: 'Arizona' },
  { value: 'AR', label: 'Arkansas' }, { value: 'CA', label: 'California' }, { value: 'CO', label: 'Colorado' },
  { value: 'CT', label: 'Connecticut' }, { value: 'DE', label: 'Delaware' }, { value: 'FL', label: 'Florida' },
  { value: 'GA', label: 'Georgia' }, { value: 'HI', label: 'Hawaii' }, { value: 'ID', label: 'Idaho' },
  { value: 'IL', label: 'Illinois' }, { value: 'IN', label: 'Indiana' }, { value: 'IA', label: 'Iowa' },
  { value: 'KS', label: 'Kansas' }, { value: 'KY', label: 'Kentucky' }, { value: 'LA', label: 'Louisiana' },
  { value: 'ME', label: 'Maine' }, { value: 'MD', label: 'Maryland' }, { value: 'MA', label: 'Massachusetts' },
  { value: 'MI', label: 'Michigan' }, { value: 'MN', label: 'Minnesota' }, { value: 'MS', label: 'Mississippi' },
  { value: 'MO', label: 'Missouri' }, { value: 'MT', label: 'Montana' }, { value: 'NE', label: 'Nebraska' },
  { value: 'NV', label: 'Nevada' }, { value: 'NH', label: 'New Hampshire' }, { value: 'NJ', label: 'New Jersey' },
  { value: 'NM', label: 'New Mexico' }, { value: 'NY', label: 'New York' }, { value: 'NC', label: 'North Carolina' },
  { value: 'ND', label: 'North Dakota' }, { value: 'OH', label: 'Ohio' }, { value: 'OK', label: 'Oklahoma' },
  { value: 'OR', label: 'Oregon' }, { value: 'PA', label: 'Pennsylvania' }, { value: 'RI', label: 'Rhode Island' },
  { value: 'SC', label: 'South Carolina' }, { value: 'SD', label: 'South Dakota' }, { value: 'TN', label: 'Tennessee' },
  { value: 'TX', label: 'Texas' }, { value: 'UT', label: 'Utah' }, { value: 'VT', label: 'Vermont' },
  { value: 'VA', label: 'Virginia' }, { value: 'WA', label: 'Washington' }, { value: 'WV', label: 'West Virginia' },
  { value: 'WI', label: 'Wisconsin' }, { value: 'WY', label: 'Wyoming' },
];

const PAY_ADJUSTMENT_STATES = ['LA', 'IN', 'IA', 'MN', 'PA', 'OH', 'NE']; // States with pay calc support

const STATE_CONFIGURATION_PRESETS = {
  NE: {
    label: 'Nebraska Section 502',
    iri_threshold_type_a: 96,
    iri_threshold_type_b: 96,
    iri_color_scale: [
      { label: 'Bonus', max: 56 },
      { label: 'Full', max: 68 },
      { label: 'Minor Deduct', max: 86 },
      { label: 'Major Deduct', max: 96 },
      { label: 'Rework Required', max: 9999 },
    ],
  },
};

// Color palettes by range count (2-5 ranges)
const COLOR_PALETTES = {
  2: ['#2EFF2E', '#D50000'],
  3: ['#2EFF2E', '#FFEB3B', '#D50000'],
  4: ['#2EFF2E', '#00A300', '#FFEB3B', '#D50000'],
  5: ['#2EFF2E', '#00A300', '#FFEB3B', '#FFA500', '#D50000'],
};

const getColorsForRangeCount = (count) => {
  return COLOR_PALETTES[count] || COLOR_PALETTES[5];
};

const cloneScale = (scale) => scale.map(range => ({ ...range }));

const clonePreset = (preset) => ({
  iri_threshold_type_a: preset.iri_threshold_type_a,
  iri_threshold_type_b: preset.iri_threshold_type_b,
  iri_color_scale: cloneScale(preset.iri_color_scale),
});

const scaleMatches = (currentScale, presetScale) => {
  if (!Array.isArray(currentScale) || currentScale.length !== presetScale.length) return false;
  return presetScale.every((presetRange, index) => {
    const currentRange = currentScale[index];
    return currentRange?.label === presetRange.label && Number(currentRange?.max) === presetRange.max;
  });
};

const presetMatches = (iriData, preset) => {
  return Number(iriData?.iri_threshold_type_a) === preset.iri_threshold_type_a &&
    Number(iriData?.iri_threshold_type_b) === preset.iri_threshold_type_b &&
    scaleMatches(iriData?.iri_color_scale, preset.iri_color_scale);
};

function classNames(...classes) {
  return classes.filter(Boolean).join(' ');
}

// HeadlessUI State Combobox
const StateCombobox = ({ value, onChange }) => {
  const [query, setQuery] = useState('');
  const buttonRef = React.useRef(null);

  const selectedState = US_STATES.find(s => s.value === value) || null;

  const filteredStates = query === ''
    ? US_STATES
    : US_STATES.filter((state) => {
        const searchLower = query.toLowerCase();
        return state.label.toLowerCase().includes(searchLower) ||
               state.value.toLowerCase().includes(searchLower);
      });

  return (
    <Combobox
      as="div"
      value={selectedState}
      onChange={(state) => {
        setQuery('');
        onChange(state?.value || '');
      }}
    >
      <div className="relative">
        <ComboboxInput
          className="block w-full rounded-sm bg-white py-2 pr-10 pl-3 text-sm text-gray-900 border border-gray-300 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500"
          onChange={(event) => setQuery(event.target.value)}
          onFocus={() => buttonRef.current?.click()}
          onBlur={() => setQuery('')}
          displayValue={(state) => state?.label || ''}
          placeholder="Search states..."
        />
        <ComboboxButton ref={buttonRef} className="absolute inset-y-0 right-0 flex items-center rounded-r-sm px-2 focus:outline-none">
          <ChevronDownIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />
        </ComboboxButton>

        <ComboboxOptions
          transition
          className="absolute z-50 mt-1 max-h-60 w-full overflow-auto rounded-sm bg-white py-1 text-sm shadow-lg ring-1 ring-black/5 focus:outline-none"
        >
          {filteredStates.length === 0 && query !== '' ? (
            <div className="px-3 py-2 text-gray-500">No states found</div>
          ) : (
            filteredStates.map((state) => (
              <ComboboxOption
                key={state.value}
                value={state}
                className="cursor-pointer select-none px-3 py-2 text-gray-900 data-[focus]:bg-red-50 data-[focus]:text-red-900"
              >
                <div className="flex items-center">
                  <span
                    className={classNames(
                      'inline-block h-2 w-2 shrink-0 rounded-full',
                      PAY_ADJUSTMENT_STATES.includes(state.value) ? 'bg-green-500' : 'bg-gray-300'
                    )}
                    aria-hidden="true"
                  />
                  <span className="ml-3 block truncate">
                    {state.label}
                    {PAY_ADJUSTMENT_STATES.includes(state.value) && (
                      <span className="sr-only"> has pay calculator support</span>
                    )}
                  </span>
                  {state.value === value && (
                    <Check className="ml-auto h-4 w-4 text-red-600" />
                  )}
                </div>
              </ComboboxOption>
            ))
          )}
        </ComboboxOptions>
      </div>
    </Combobox>
  );
};

const IRIRangeEditor = ({ ranges, onChange, thresholdA }) => {
  const colors = getColorsForRangeCount(ranges.length);
  const canAddRange = ranges.length < 5;
  const canRemoveRange = ranges.length > 2;

  const handleLabelChange = (index, value) => {
    const newRanges = ranges.map((r, i) =>
      i === index ? { ...r, label: value } : r
    );
    onChange({ target: { name: 'iri_color_scale', value: newRanges } });
  };

  const handleMaxChange = (index, value) => {
    const newRanges = ranges.map((r, i) =>
      i === index ? { ...r, max: parseFloat(value) || 0 } : r
    );
    onChange({ target: { name: 'iri_color_scale', value: newRanges } });
  };

  const handleAddRange = () => {
    if (!canAddRange) return;

    // Insert new range before the last one
    const lastIndex = ranges.length - 1;
    const secondToLastIndex = lastIndex - 1;

    // New range gets inserted between second-to-last and last
    // Its max will become the threshold-linked one
    // Previous second-to-last needs a sensible max
    const prevSecondToLast = ranges[secondToLastIndex];
    const newMax = Math.round((prevSecondToLast.max + thresholdA) / 2);

    const newRanges = [
      ...ranges.slice(0, lastIndex),
      { label: 'New Range', max: thresholdA }, // This becomes the new threshold-linked one
      { ...ranges[lastIndex] }, // Last range (∞)
    ];

    // Update the previous second-to-last to have a reasonable max
    newRanges[secondToLastIndex] = { ...prevSecondToLast, max: newMax };

    onChange({ target: { name: 'iri_color_scale', value: newRanges } });
  };

  const handleRemoveRange = (index) => {
    if (!canRemoveRange) return;
    if (index === 0 || index === ranges.length - 1) return; // Can't remove first or last

    const newRanges = ranges.filter((_, i) => i !== index);
    onChange({ target: { name: 'iri_color_scale', value: newRanges } });
  };

  const getMinValue = (index) => {
    if (index === 0) return 0;
    if (index === ranges.length - 1) return thresholdA; // Last range starts at threshold
    return ranges[index - 1]?.max || 0;
  };

  const isLastRange = (index) => index === ranges.length - 1;
  const isSecondToLastRange = (index) => index === ranges.length - 2;
  const isFirstRange = (index) => index === 0;

  return (
    <div className="space-y-2">
      {ranges.map((range, index) => {
        const minVal = getMinValue(index);
        const isLast = isLastRange(index);
        const isSecondToLast = isSecondToLastRange(index);
        const isFirst = isFirstRange(index);
        const canDelete = canRemoveRange && !isFirst && !isLast;

        return (
          <div key={index} className="grid grid-cols-12 gap-2 items-center">
            {/* Color swatch */}
            <div className="col-span-1 flex justify-center">
              <div
                className="h-4 w-6 rounded-sm border border-gray-300"
                style={{ backgroundColor: colors[index] }}
              />
            </div>

            {/* Label input */}
            <div className="col-span-4">
              <input
                type="text"
                value={range.label}
                onChange={(e) => handleLabelChange(index, e.target.value)}
                className="block w-full px-2 py-1 border border-gray-300 rounded-sm text-sm focus:ring-2 focus:ring-blue-500"
              />
            </div>

            {/* Range display/input */}
            <div className="col-span-6 flex items-center gap-1 text-sm">
              <span className="text-gray-500 w-12 text-right font-mono">{minVal}</span>
              <span className="text-gray-400">–</span>
              {isLast ? (
                <span className="text-gray-500 font-mono">∞</span>
              ) : isSecondToLast ? (
                <span className="px-2 py-1 bg-blue-50 border border-blue-200 rounded-sm text-blue-700 font-mono font-medium">
                  {thresholdA}
                </span>
              ) : (
                <input
                  type="number"
                  value={range.max}
                  onChange={(e) => handleMaxChange(index, e.target.value)}
                  className="w-16 px-2 py-1 border border-gray-300 rounded-sm text-sm font-mono focus:ring-2 focus:ring-blue-500"
                />
              )}
              <span className="text-gray-400 text-xs ml-1">in/mi</span>
            </div>

            {/* Delete button */}
            <div className="col-span-1 flex justify-center">
              {canDelete ? (
                <button
                  type="button"
                  onClick={() => handleRemoveRange(index)}
                  className="p-1 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded transition-colors"
                  title="Remove range"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              ) : (
                <div className="w-6" /> // Spacer
              )}
            </div>
          </div>
        );
      })}

      {/* Add Range button */}
      {canAddRange && (
        <button
          type="button"
          onClick={handleAddRange}
          className="mt-3 inline-flex items-center gap-1.5 px-3 py-1.5 text-sm text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-sm transition-colors"
        >
          <PlusIcon className="h-4 w-4" />
          Add Range
        </button>
      )}

      <p className="text-xs text-gray-500 mt-3 flex items-center gap-1">
        <span className="inline-block w-3 h-3 bg-blue-50 border border-blue-200 rounded-sm"></span>
        Linked to Fixed Interval Threshold
      </p>
    </div>
  );
};

const IRIRangeViewer = ({ ranges, thresholdA }) => {
  const colors = getColorsForRangeCount(ranges.length);

  const displayRanges = ranges.map((range, index) => {
    const isLast = index === ranges.length - 1;
    const prevMax = index === 0 ? 0 : (isLast ? thresholdA : ranges[index - 1].max);
    const currentMax = index === ranges.length - 2 ? thresholdA : range.max;

    return {
      label: range.label,
      color: colors[index],
      rangeText: isLast ? `> ${prevMax}` : `${prevMax} – ${currentMax}`,
    };
  });

  return (
    <div className="space-y-1.5">
      {displayRanges.map((range, index) => (
        <div key={index} className="flex items-center gap-3">
          <div
            className="h-3 w-6 rounded-sm border border-gray-300 flex-shrink-0"
            style={{ backgroundColor: range.color }}
          />
          <span className="text-sm text-gray-900 w-24">{range.label}</span>
          <span className="text-sm text-gray-500 font-mono">{range.rangeText}</span>
        </div>
      ))}
    </div>
  );
};

export default function ProjectConfiguration({ project, config, onConfigSave, onProjectSave, onProjectDelete }) {
  const [searchParams, setSearchParams] = useSearchParams();
  const [iriData, setIriData] = useState({});
  const [savedData, setSavedData] = useState({});
  const [isEditing, setIsEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveStatus, setSaveStatus] = useState({ message: '', type: '' });
  const [dismissedStatePreset, setDismissedStatePreset] = useState(null);
  const [showStatePresetModal, setShowStatePresetModal] = useState(false);

  // State field (stored on project, not config)
  const [projectState, setProjectState] = useState(project?.state || '');
  const [savedProjectState, setSavedProjectState] = useState(project?.state || '');

  const defaultIriScale = [
    { label: 'Excellent', max: 40 },
    { label: 'Very Good', max: 60 },
    { label: 'Satisfactory', max: 90 },
    { label: 'Poor', max: 160 },
    { label: 'Very Poor', max: 9999 }
  ];

  useEffect(() => {
    // Validate the scale: must be array with 2-5 items
    const isValidScale = config?.iri_color_scale &&
      Array.isArray(config.iri_color_scale) &&
      config.iri_color_scale.length >= 2 &&
      config.iri_color_scale.length <= 5;

    const iriScale = isValidScale ? config.iri_color_scale : defaultIriScale;

    const data = {
      iri_threshold_type_a: config?.iri_threshold_type_a || 160.0,
      iri_threshold_type_b: config?.iri_threshold_type_b || 170.0,
      iri_color_scale: iriScale,
    };

    setIriData(data);
    setSavedData(JSON.parse(JSON.stringify(data)));
    setProjectState(project?.state || '');
    setSavedProjectState(project?.state || '');
    setDismissedStatePreset(null);
    setShowStatePresetModal(false);
  }, [project, config]);

  const handleChange = useCallback((e) => {
    const { name, value, type } = e.target;
    const processedValue = type === 'number' ? parseFloat(value) || 0 : value;
    setIriData(prev => ({ ...prev, [name]: processedValue }));
    setSaveStatus({ message: '', type: '' });
  }, []);

  const handleResetScale = () => {
    setIriData(prev => ({
      ...prev,
      iri_color_scale: [
        { label: 'Excellent', max: 40 },
        { label: 'Very Good', max: 60 },
        { label: 'Satisfactory', max: 90 },
        { label: 'Poor', max: prev.iri_threshold_type_a || 160 },
        { label: 'Very Poor', max: 9999 }
      ]
    }));
  };

  const handleStateChange = useCallback((value) => {
    setProjectState(value);
    setDismissedStatePreset(null);
    setSaveStatus({ message: '', type: '' });
  }, []);

  const handleApplyStatePreset = () => {
    const preset = STATE_CONFIGURATION_PRESETS[projectState];
    if (!preset) return;

    setIriData(prev => ({
      ...prev,
      ...clonePreset(preset),
    }));
    setShowStatePresetModal(false);
    setSaveStatus({ message: '', type: '' });
  };

  const handleDismissStatePreset = () => {
    setDismissedStatePreset(projectState);
    setShowStatePresetModal(false);
  };

  const handleCancel = () => {
    setIriData(JSON.parse(JSON.stringify(savedData)));
    setProjectState(savedProjectState);
    setDismissedStatePreset(null);
    setShowStatePresetModal(false);
    setIsEditing(false);
    setSaveStatus({ message: '', type: '' });
  };

  const handleSave = async () => {
    setSaving(true);
    setSaveStatus({ message: '', type: '' });

    try {
      const ranges = iriData.iri_color_scale;
      const threshold = iriData.iri_threshold_type_a;

      // Validation: each range max must be less than the next (excluding last which is ∞)
      for (let i = 0; i < ranges.length - 2; i++) {
        const currentMax = ranges[i].max;
        const nextMax = i === ranges.length - 3 ? threshold : ranges[i + 1].max;

        if (currentMax >= nextMax) {
          throw new Error(`"${ranges[i].label}" max (${currentMax}) must be less than "${ranges[i + 1].label}" (${nextMax}).`);
        }
      }

      // Validate second-to-last is less than threshold
      const secondToLastIndex = ranges.length - 2;
      if (secondToLastIndex > 0) {
        const prevMax = ranges[secondToLastIndex - 1].max;
        if (prevMax >= threshold) {
          throw new Error(`"${ranges[secondToLastIndex - 1].label}" max (${prevMax}) must be less than Fixed Interval Threshold (${threshold}).`);
        }
      }

      // Sync second-to-last max to threshold before saving
      const scaleToSave = JSON.parse(JSON.stringify(ranges));
      scaleToSave[secondToLastIndex].max = threshold;

      const dataToSave = {
        iri_threshold_type_a: iriData.iri_threshold_type_a,
        iri_threshold_type_b: iriData.iri_threshold_type_b,
        iri_color_scale: scaleToSave
      };

      const updatedConfig = await upsertProjectConfiguration(project.id, dataToSave);

      // Save state to project if changed
      if (projectState !== savedProjectState) {
	        const updatedProject = await updateProject(project.id, { state: projectState });
	        if (onProjectSave) onProjectSave({ ...project, ...updatedProject });
        setSavedProjectState(projectState);
      }

      if (onConfigSave) onConfigSave(updatedConfig);

      setSavedData(JSON.parse(JSON.stringify({ ...iriData, iri_color_scale: scaleToSave })));
      setIsEditing(false);
      setSaveStatus({ message: 'Saved!', type: 'success' });

      setTimeout(() => setSaveStatus({ message: '', type: '' }), 2000);
    } catch (error) {
      console.error("Failed to save:", error);
      setSaveStatus({ message: error.message || 'Failed to save.', type: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const hasPayAdjustmentSupport = PAY_ADJUSTMENT_STATES.includes(projectState);
  const statePreset = STATE_CONFIGURATION_PRESETS[projectState];
  const canWriteProject = project?.project_access_role === 'owner' || project?.project_access_role === 'dot_admin';
  const isProjectOwner = project?.project_access_role === 'owner';
  const requestedSettingsTab = searchParams.get('settings') || 'config';
  const settingsTabs = [
    { id: 'config', label: 'Configuration' },
    { id: 'sharing', label: 'Sharing' },
    ...(isProjectOwner ? [{ id: 'danger', label: 'Danger Zone' }] : []),
  ];
  const activeSettingsTab = settingsTabs.some(tab => tab.id === requestedSettingsTab)
    ? requestedSettingsTab
    : 'config';
  const showStatePresetPrompt = isEditing &&
    statePreset &&
    dismissedStatePreset !== projectState &&
    !presetMatches(iriData, statePreset);

  const handleSettingsTabChange = useCallback((tabId) => {
    const nextParams = new URLSearchParams(searchParams);
    nextParams.set('tab', 'settings');
    if (tabId === 'config') {
      nextParams.delete('settings');
    } else {
      nextParams.set('settings', tabId);
    }
    setSearchParams(nextParams);
  }, [searchParams, setSearchParams]);

  useEffect(() => {
    if (activeSettingsTab !== requestedSettingsTab) {
      handleSettingsTabChange('config');
    }
  }, [activeSettingsTab, requestedSettingsTab, handleSettingsTabChange]);

  useEffect(() => {
    setShowStatePresetModal(Boolean(showStatePresetPrompt));
  }, [showStatePresetPrompt]);

  return (
    <div>
      {showStatePresetModal && statePreset && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="fixed inset-0 bg-black/40" onClick={handleDismissStatePreset} />
          <div className="flex min-h-full items-center justify-center p-4">
            <div className="relative w-full max-w-md bg-white shadow-xl rounded-sm">
              <div className="px-4 py-3 border-b border-gray-200">
                <h3 className="text-base font-semibold text-gray-900">
                  Apply {statePreset.label} settings?
                </h3>
              </div>
              <div className="px-4 py-4">
                <p className="text-sm text-gray-700">
                  This will update this project&apos;s thresholds and map legend for Nebraska smoothness review.
                </p>
                <div className="mt-3 rounded-sm bg-gray-50 border border-gray-200 px-3 py-2 text-xs text-gray-600 space-y-1">
                  <p>Fixed Interval Threshold: <span className="font-mono text-gray-900">96 in/mi</span></p>
                  <p>ALR Threshold: <span className="font-mono text-gray-900">96 in/mi</span></p>
                  <p>Legend: Bonus, Full, Minor Deduct, Major Deduct, Rework Required</p>
                </div>
                <p className="mt-3 text-xs text-gray-500">
                  Nothing is saved until you click Save.
                </p>
              </div>
              <div className="flex items-center justify-end gap-2 border-t border-gray-200 px-4 py-3">
                <button
                  type="button"
                  onClick={handleDismissStatePreset}
                  className="px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-sm"
                >
                  Keep Current
                </button>
                <button
                  type="button"
                  onClick={handleApplyStatePreset}
                  className="px-3 py-1.5 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-sm"
                >
                  Apply Defaults
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="h-14 bg-white border-b border-gray-200">
        <div className="h-full px-6 flex items-end">
          <nav className="h-full flex items-end gap-1" aria-label="Project settings sections">
            {settingsTabs.map(tab => {
              const active = activeSettingsTab === tab.id;
              return (
                <button
                  key={tab.id}
                  type="button"
                  onClick={() => handleSettingsTabChange(tab.id)}
                  className={classNames(
                    'h-11 inline-flex items-center px-4 text-sm font-medium border-b-2 transition-colors',
                    active
                      ? 'border-red-600 text-gray-900'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:bg-gray-50'
                  )}
                >
                  {tab.label}
                </button>
              );
            })}
          </nav>
        </div>
      </div>

      {activeSettingsTab === 'config' && (
        <div className="bg-white">
        <div className="bg-gray-50 px-6 py-4 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-medium text-gray-900">Project Configuration</h3>
              <p className="text-sm text-gray-500 mt-0.5">Roughness thresholds, color scale, and pay settings</p>
            </div>

            {isEditing ? (
              <div className="flex items-center gap-2">
                <button
                  onClick={handleCancel}
                  disabled={saving}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSave}
                  disabled={saving}
                  className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700 disabled:opacity-50"
                >
                  <Check className="h-4 w-4" />
                  {saving ? 'Saving...' : 'Save'}
                </button>
              </div>
            ) : (
              <div className="flex items-center gap-3">
                {saveStatus.type === 'success' && (
                  <span className="text-sm text-green-600 font-medium">{saveStatus.message}</span>
                )}
	                {canWriteProject && (
	                  <button
	                    onClick={() => setIsEditing(true)}
	                    className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
	                  >
	                    <PencilIcon className="h-4 w-4" />
	                    Edit
	                  </button>
	                )}
              </div>
            )}
          </div>
        </div>

        <div className="px-6 py-6">
          {saveStatus.type === 'error' && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-sm">
              <p className="text-sm text-red-700">{saveStatus.message}</p>
            </div>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Left Column - Thresholds + State */}
            <div className="space-y-6">
              {/* State Dropdown */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  State
                  {hasPayAdjustmentSupport && (
                    <span className="ml-2 text-xs text-green-600 font-normal">✓ Pay calculator available</span>
                  )}
                </label>
                <p className="text-xs text-gray-500 mb-2">
                  Used for state-specific pay adjustment calculations
                </p>
                {isEditing ? (
                  <>
                    <StateCombobox
                      value={projectState}
                      onChange={handleStateChange}
                    />
                    {showStatePresetPrompt && (
                      <div className="mt-3 rounded-sm border border-blue-200 bg-blue-50 px-3 py-2">
                        <div className="flex items-start justify-between gap-3">
                          <div>
                            <p className="text-sm font-medium text-blue-900">
                              Apply {statePreset.label} thresholds and legend?
                            </p>
                            <p className="mt-0.5 text-xs text-blue-700">
                              Sets thresholds to 96 in/mi and legend ranges to Bonus, Full, Minor Deduct, Major Deduct, and Rework Required.
                            </p>
                          </div>
                          <div className="flex items-center gap-2 flex-shrink-0">
                            <button
                              type="button"
                              onClick={handleApplyStatePreset}
                              className="px-2.5 py-1 text-xs font-medium text-white bg-blue-600 rounded-sm hover:bg-blue-700"
                            >
                              Apply Defaults
                            </button>
                            <button
                              type="button"
                              onClick={() => setDismissedStatePreset(projectState)}
                              className="px-2.5 py-1 text-xs font-medium text-blue-700 hover:bg-blue-100 rounded-sm"
                            >
                              Keep Current
                            </button>
                          </div>
                        </div>
                      </div>
                    )}
                  </>
                ) : (
                  <div className="text-sm text-gray-900 py-2 flex items-center gap-2">
                    {projectState && (
                      <span
                        className={classNames(
                          'inline-block h-2 w-2 shrink-0 rounded-full',
                          PAY_ADJUSTMENT_STATES.includes(projectState) ? 'bg-green-500' : 'bg-gray-300'
                        )}
                      />
                    )}
                    {US_STATES.find(s => s.value === projectState)?.label || 'Not specified'}
                  </div>
                )}
              </div>

              {/* Fixed Interval Threshold */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Fixed Interval Threshold
                </label>
                <p className="text-xs text-gray-500 mb-2">
                  528 ft segments • Segment MRI for pay adjustments
                </p>
                {isEditing ? (
                  <div className="relative">
                    <input
                      name="iri_threshold_type_a"
                      type="number"
                      value={iriData.iri_threshold_type_a || ''}
                      onChange={handleChange}
                      className="block w-full text-4xl font-bold text-gray-900 border-0 border-b-4 border-gray-300 focus:border-red-500 focus:ring-0 pb-2 bg-transparent"
                    />
                    <span className="absolute bottom-2 right-0 text-lg text-gray-500 font-medium">
                      in/mi
                    </span>
                  </div>
                ) : (
                  <div className="flex items-baseline gap-2">
                    <span className="text-4xl font-bold text-gray-900">{iriData.iri_threshold_type_a || '—'}</span>
                    <span className="text-lg text-gray-500">in/mi</span>
                  </div>
                )}
              </div>

              {/* ALR Threshold */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  ALR Threshold
                </label>
                <p className="text-xs text-gray-500 mb-2">
                  25 ft continuous • Localized roughness requiring correction
                </p>
                {isEditing ? (
                  <div className="relative">
                    <input
                      name="iri_threshold_type_b"
                      type="number"
                      value={iriData.iri_threshold_type_b || ''}
                      onChange={handleChange}
                      className="block w-full text-4xl font-bold text-gray-900 border-0 border-b-4 border-gray-300 focus:border-red-500 focus:ring-0 pb-2 bg-transparent"
                    />
                    <span className="absolute bottom-2 right-0 text-lg text-gray-500 font-medium">
                      in/mi
                    </span>
                  </div>
                ) : (
                  <div className="flex items-baseline gap-2">
                    <span className="text-4xl font-bold text-gray-900">{iriData.iri_threshold_type_b || '—'}</span>
                    <span className="text-lg text-gray-500">in/mi</span>
                  </div>
                )}
              </div>
            </div>

            {/* Right Column - Color Scale */}
            <div>
              <div className="flex items-center justify-between mb-3">
                <h4 className="text-sm font-medium text-gray-700">Color Scale</h4>
                {isEditing && (
                  <button
                    onClick={handleResetScale}
                    type="button"
                    className="text-xs text-gray-600 hover:text-gray-900 underline focus:outline-none"
                  >
                    Reset to Default
                  </button>
                )}
              </div>

              {isEditing ? (
                iriData.iri_color_scale && (
                  <IRIRangeEditor
                    ranges={iriData.iri_color_scale}
                    onChange={handleChange}
                    thresholdA={iriData.iri_threshold_type_a}
                  />
                )
              ) : (
                iriData.iri_color_scale && (
                  <IRIRangeViewer
                    ranges={iriData.iri_color_scale}
                    thresholdA={iriData.iri_threshold_type_a}
                  />
                )
              )}
            </div>
          </div>
        </div>
      </div>
      )}

      {activeSettingsTab === 'sharing' && (
        <ProjectSharing project={project} />
      )}

      {/* Danger Zone */}
      {activeSettingsTab === 'danger' && isProjectOwner && (
        <div className="bg-white border-y border-red-200 overflow-hidden">
          <div className="px-6 py-4 border-b border-red-200 bg-red-50">
            <div className="flex items-center">
              <ExclamationTriangleIcon className="h-5 w-5 text-red-600 mr-2" />
              <h3 className="text-lg font-medium text-red-900">Danger Zone</h3>
            </div>
            <p className="text-sm text-red-700 mt-0.5">Irreversible and destructive actions</p>
          </div>

          <div className="px-6 py-6">
            <div className="flex items-center justify-between">
              <div>
                <h4 className="text-sm font-medium text-gray-900">Delete this project</h4>
                <p className="text-sm text-gray-500 mt-1">
                  Once you delete a project, there is no going back. All sessions and data will be permanently removed.
                </p>
              </div>
              <button
                onClick={onProjectDelete}
                className="ml-6 inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 transition-colors rounded-md"
              >
                <XIcon className="h-4 w-4 mr-2" />
                Delete Project
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
